import { useState, type CSSProperties } from "react";
import { ColumnDef } from "@tanstack/react-table";

/**
 * Same transparency backdrop as GeneratedBlueprintPanel.tsx
 * (`src/containers/Products/components/GeneratedBlueprintPanel.tsx`,
 * exported as `CHECKERBOARD_STYLE`) -- duplicated in value rather than
 * imported, to avoid pulling the whole admin-only blueprint
 * generate/re-generate mutation panel (and its `@apollo/client` mutation,
 * icons, Button/Badge/Skeleton/CopyButton deps) into this pricing table's
 * column module for the sake of a 6-line style object. Keep in sync with
 * that file if the checkerboard styling ever changes.
 */
const CHECKERBOARD_STYLE: CSSProperties = {
  backgroundImage:
    "linear-gradient(45deg, #e5e7eb 25%, transparent 25%), " +
    "linear-gradient(-45deg, #e5e7eb 25%, transparent 25%), " +
    "linear-gradient(45deg, transparent 75%, #e5e7eb 75%), " +
    "linear-gradient(-45deg, transparent 75%, #e5e7eb 75%)",
  backgroundSize: "16px 16px",
  backgroundPosition: "0 0, 0 8px, 8px -8px, -8px 0px",
  backgroundColor: "#ffffff",
};

export type SofaModule = {
  id: string;
  code: string;
  name: string;
  /**
   * Stored, pre-rendered PNG (1 px/cm) -- see GeneratedBlueprintPanel.tsx
   * for the full contract. This query intentionally selects only this
   * field (no `type`/`dimensions`), so there is no live-Konva fallback
   * available here -- see BlueprintThumbnailCell below.
   */
  blueprint_thumbnail_url?: string | null;
  form_price_fabric_category: Array<{
    price: number;
    // NOTE: this is the backend's actual (typo'd) relation name. It was
    // previously declared here as `fabric_price_category`, which every
    // accessor below silently worked around via an `any` cast -- the
    // declared type never matched what accessors actually read. Renamed
    // to match reality; the `any` casts are left in place because
    // `group_number` is declared `string` here while accessors compare it
    // to a number (`=== 1`), which is a second, separate type mismatch not
    // touched by this change (see report).
    fabrice_price_category: {
      group_number: string;
    };
  }>;
};

/**
 * Leading Blueprint column cell for the pricelist Sofa Modules table.
 *
 * This table's query (GET_PRICELIST_SOFA_MODULES) deliberately does not
 * select `type`/`dimensions` -- this page's job is pricing, not visual QA
 * -- so there is no live Konva render to fall back to here. Missing or
 * failed thumbnails degrade to a neutral static placeholder, never a live
 * drawing.
 *
 * Failure is tracked by URL (not a boolean) because this table is
 * server-paginated (`DataTableManual`) and reuses row/cell instances by
 * index across pages -- see the matching note in SofaFormsTable.tsx.
 */
export function BlueprintThumbnailCell({ url }: { url?: string | null }) {
  const [failedUrl, setFailedUrl] = useState<string | null>(null);
  const showImage = !!url && failedUrl !== url;

  return (
    <div
      className="flex h-10 w-10 items-center justify-center overflow-hidden rounded border"
      style={showImage ? CHECKERBOARD_STYLE : undefined}
    >
      {showImage ? (
        // eslint-disable-next-line jsx-a11y/alt-text
        <img
          src={url!}
          alt="Module blueprint thumbnail"
          loading="lazy"
          className="max-h-full max-w-full object-contain"
          onError={() => setFailedUrl(url!)}
        />
      ) : (
        <div
          className="h-full w-full bg-muted"
          aria-hidden="true"
          title="No blueprint image available"
        />
      )}
    </div>
  );
}

export const columns: ColumnDef<SofaModule>[] = [
  {
    id: "blueprint",
    header: () => "Blueprint",
    cell: ({ row }) => (
      <BlueprintThumbnailCell url={row.original.blueprint_thumbnail_url} />
    ),
    enableSorting: false,
  },
  {
    accessorKey: "code",
    header: () => "Code",
  },
  {
    accessorKey: "name",
    header: () => "Name",
  },
  {
    accessorKey: "cat_1",
    header: () => <div className="text-right">CAT 1</div>,
    cell: ({ row }) => {
      const price =
        row.original.form_price_fabric_category.find(
          (f: any) => f.fabrice_price_category.group_number === 1
        )?.price || "-";
      return <div className="text-right">{price}</div>;
    },
  },
  {
    accessorKey: "cat_2",
    header: () => <div className="text-right">CAT 2</div>,
    cell: ({ row }) => {
      const price =
        row.original.form_price_fabric_category.find(
          (f: any) => f.fabrice_price_category.group_number === 2
        )?.price || "-";
      return <div className="text-right">{price}</div>;
    },
  },
  {
    accessorKey: "cat_3",
    header: () => <div className="text-right">CAT 3</div>,
    cell: ({ row }) => {
      const price =
        row.original.form_price_fabric_category.find(
          (f: any) => f.fabrice_price_category.group_number === 3
        )?.price || "-";
      return <div className="text-right">{price}</div>;
    },
  },
  {
    accessorKey: "cat_4",
    header: () => <div className="text-right">CAT 4</div>,
    cell: ({ row }) => {
      const price =
        row.original.form_price_fabric_category.find(
          (f: any) => f.fabrice_price_category.group_number === 4
        )?.price || "-";
      return <div className="text-right">{price}</div>;
    },
  },
  {
    accessorKey: "cat_5",
    header: () => <div className="text-right">CAT 5</div>,
    cell: ({ row }) => {
      const price =
        row.original.form_price_fabric_category.find(
          (f: any) => f.fabrice_price_category.group_number === 5
        )?.price || "-";
      return <div className="text-right">{price}</div>;
    },
  },
  {
    accessorKey: "cat_6",
    header: () => <div className="text-right">CAT 6</div>,
    cell: ({ row }) => {
      const price =
        row.original.form_price_fabric_category.find(
          (f: any) => f.fabrice_price_category.group_number === 6
        )?.price || "-";
      return <div className="text-right">{price}</div>;
    },
  },
  {
    accessorKey: "cat_7",
    header: () => <div className="text-right">CAT 7</div>,
    cell: ({ row }) => {
      const price =
        row.original.form_price_fabric_category.find(
          (f: any) => f.fabrice_price_category.group_number === 7
        )?.price || "-";
      return <div className="text-right">{price}</div>;
    },
  },
  {
    accessorKey: "cat_L1",
    header: () => <div className="text-right">L1</div>,
    cell: ({ row }) => {
      const price =
        row.original.form_price_fabric_category.find(
          (f: any) => f.fabrice_price_category.group_number === 11
        )?.price || "-";
      return <div className="text-right">{price}</div>;
    },
  },
  {
    accessorKey: "cat_L2",
    header: () => <div className="text-right">L2</div>,
    cell: ({ row }) => {
      const price =
        row.original.form_price_fabric_category.find(
          (f: any) => f.fabrice_price_category.group_number === 12
        )?.price || "-";
      return <div className="text-right">{price}</div>;
    },
  },
];
